import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Plus, 
  Trash2, 
  Check, 
  X, 
  AlertCircle, 
  Send,
  Settings,
  TestTube
} from 'lucide-react';
import api from '../services/api';

const EmailSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setError('');
      const response = await api.get('/email/settings');
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      setError('Hiba történt az e-mail beállítások betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updatedFields) => {
    try {
      setError('');
      setSuccess('');
      const response = await api.put('/email/settings', updatedFields);
      setSettings(response.data.settings);
      setSuccess('Beállítások sikeresen frissítve!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating settings:', error);
      setError(error.response?.data?.error || 'Hiba történt a beállítások frissítése során.');
    }
  };

  const addEmailRecipient = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setAddingEmail(true);
    try {
      setError('');
      const response = await api.post('/email/recipients', { email: newEmail.trim() });
      setSettings(prev => ({
        ...prev,
        recipients: response.data.recipients
      }));
      setNewEmail('');
      setSuccess('E-mail cím sikeresen hozzáadva!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding email recipient:', error);
      setError(error.response?.data?.error || 'Hiba történt az e-mail cím hozzáadása során.');
    } finally {
      setAddingEmail(false);
    }
  };

  const removeEmailRecipient = async (email) => {
    if (!window.confirm(`Biztosan eltávolítja ezt az e-mail címet: ${email}?`)) return;

    try {
      setError('');
      const response = await api.delete(`/email/recipients/${encodeURIComponent(email)}`);
      setSettings(prev => ({
        ...prev,
        recipients: response.data.recipients
      }));
      setSuccess('E-mail cím sikeresen eltávolítva!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error removing email recipient:', error);
      setError(error.response?.data?.error || 'Hiba történt az e-mail cím eltávolítása során.');
    }
  };

  const testEmailConfiguration = async () => {
    setTesting(true);
    try {
      setError('');
      await api.post('/email/test');
      setSuccess('E-mail konfiguráció működik!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error testing email configuration:', error);
      setError(error.response?.data?.error || 'E-mail konfiguráció teszt sikertelen.');
    } finally {
      setTesting(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      setError('');
      const payload = testEmail.trim() ? { testEmail: testEmail.trim() } : {};
      const response = await api.post('/email/test-send', payload);
      setSuccess(`Teszt e-mail elküldve! Message ID: ${response.data.messageId}`);
      setTimeout(() => setSuccess(''), 5000);
      setTestEmail('');
    } catch (error) {
      console.error('Error sending test email:', error);
      setError(error.response?.data?.error || 'Teszt e-mail küldése sikertelen.');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleEmailNotifications = async () => {
    await updateSettings({ enabled: !settings.enabled });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">Nem sikerült betölteni az e-mail beállításokat.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Mail className="h-6 w-6 text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900">E-mail Értesítések</h3>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-medium text-gray-900">E-mail értesítések</h4>
            <p className="text-sm text-gray-600">
              Automatikus e-mail küldése új megjegyzések esetén
            </p>
          </div>
          <button
            onClick={toggleEmailNotifications}
            className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              settings.enabled ? 'bg-primary-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${
                settings.enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* SMTP Configuration (Read-only) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
          <Settings className="h-5 w-5" />
          <span>SMTP Konfiguráció</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <label className="block text-gray-700 font-medium">SMTP Szerver:</label>
            <p className="text-gray-900">{settings.smtp.host}:{settings.smtp.port}</p>
          </div>
          <div>
            <label className="block text-gray-700 font-medium">Feladó:</label>
            <p className="text-gray-900">{settings.from}</p>
          </div>
          <div>
            <label className="block text-gray-700 font-medium">Felhasználó:</label>
            <p className="text-gray-900">{settings.smtp.auth.user}</p>
          </div>
          <div>
            <label className="block text-gray-700 font-medium">SSL:</label>
            <p className="text-gray-900">{settings.smtp.secure ? 'Engedélyezve' : 'Letiltva'}</p>
          </div>
        </div>
        
        <div className="mt-4 flex space-x-3">
          <button
            onClick={testEmailConfiguration}
            disabled={testing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <TestTube className="h-4 w-4 mr-2" />
            {testing ? 'Tesztelés...' : 'Kapcsolat Tesztelése'}
          </button>
        </div>
      </div>

      {/* Email Recipients */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">E-mail Címzettek</h4>
        <p className="text-sm text-gray-600 mb-4">
          Ezekre az e-mail címekre érkeznek az értesítések új megjegyzések esetén.
        </p>

        {/* Add New Email Form */}
        <form onSubmit={addEmailRecipient} className="mb-4">
          <div className="flex space-x-3">
            <input
              type="email"
              placeholder="E-mail cím hozzáadása..."
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={addingEmail}
            />
            <button
              type="submit"
              disabled={addingEmail || !newEmail.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4 mr-2" />
              {addingEmail ? 'Hozzáadás...' : 'Hozzáadás'}
            </button>
          </div>
        </form>

        {/* Recipients List */}
        {settings.recipients && settings.recipients.length > 0 ? (
          <div className="space-y-2">
            {settings.recipients.map((email) => (
              <div key={email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{email}</span>
                </div>
                <button
                  onClick={() => removeEmailRecipient(email)}
                  className="text-gray-400 hover:text-red-600 transition-colors"
                  title="Eltávolítás"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <Mail className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>Még nincsenek e-mail címzettek megadva.</p>
          </div>
        )}
      </div>

      {/* Test Email */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Teszt E-mail Küldése</h4>
        <p className="text-sm text-gray-600 mb-4">
          Küldjön egy teszt e-mailt a beállított címzetteknek vagy egy egyedi címre.
        </p>

        <div className="flex space-x-3">
          <input
            type="email"
            placeholder="Egyedi e-mail cím (opcionális)"
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            disabled={sendingTest}
          />
          <button
            onClick={sendTestEmail}
            disabled={sendingTest || !settings.enabled}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4 mr-2" />
            {sendingTest ? 'Küldés...' : 'Teszt Küldése'}
          </button>
        </div>
        
        {!settings.enabled && (
          <p className="text-xs text-gray-500 mt-2">
            Az e-mail értesítések le vannak tiltva.
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailSettings; 